import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useCart } from "../context/AppContext";
import { CheckCircle2, Loader2, Bitcoin, AlertTriangle, Clock, ExternalLink } from "lucide-react";

const TOKEN_PREFIX = "bl_crypto_";
const POLL_MS = 5000;
const FINAL = new Set(["paid", "expired", "review"]);

const COPY = {
  paid:    { eyebrow: "Confirmed",       title: "Payment Received",   body: "Your Bitcoin payment has settled. We've emailed your receipt and the order is queued for dispatch." },
  pending: { eyebrow: "Awaiting Bitcoin", title: "Waiting for Payment", body: "We're watching the blockchain for your payment. This page updates automatically — you can safely close it; your receipt arrives by email once it settles." },
  expired: { eyebrow: "Expired",         title: "Invoice Expired",    body: "The Bitcoin invoice timed out before payment arrived. Nothing was charged — your cart is intact, just check out again." },
  review:  { eyebrow: "Under Review",    title: "We're Checking This", body: "Your payment needs a manual look (partial, late or over-paid). We'll email you within one business day — no action needed." },
  uncertain: { eyebrow: "Reserved",      title: "Order Reserved",     body: "We couldn't reach the Bitcoin payment provider just now, so no invoice was issued. Your order is reserved and our team will email you a payment link shortly — nothing to do on your side." },
  unknown: { eyebrow: "Not Found",       title: "Order Not Found",    body: "We couldn't load this Bitcoin order. If you've just paid, your receipt will arrive by email; otherwise use Track Order with the reference from your confirmation." },
};

function deriveState(status, error) {
  if (error) return "unknown";
  if (!status) return "pending";
  if (status.state === "paid") return "paid";
  if (status.state === "pending" && ["uncertain", "failed"].includes(status.create_status)) return "uncertain";
  return status.state in COPY ? status.state : "pending";
}

function useCryptoStatus(orderId) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    const token = sessionStorage.getItem(TOKEN_PREFIX + orderId);
    let timer;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/payments/crypto/status/${orderId}`, {
          withCredentials: true, headers: token ? { "X-Order-Token": token } : {},
        });
        if (stopped) return;
        setStatus(r.data);
        if (!FINAL.has(r.data.state)) timer = setTimeout(tick, POLL_MS);
      } catch (err) {
        if (stopped) return;
        setError(err?.response?.status === 403
          ? "We can't show this order in this browser. Check your email for the receipt or use Track Order."
          : "We couldn't load this order right now.");
      }
    };
    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [orderId]);
  return { status, error };
}

export default function CryptoReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { status, error } = useCryptoStatus(orderId);
  const { clear } = useCart();

  const state = deriveState(status, error);
  useEffect(() => { if (state === "paid") clear(); }, [state, clear]);
  const copy = COPY[state];

  const Icon = { paid: CheckCircle2, expired: Clock, review: AlertTriangle, uncertain: AlertTriangle, unknown: AlertTriangle }[state] || Loader2;
  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-6">
      <div className="max-w-lg mx-auto text-center" data-testid="crypto-return">
        <Icon size={56} className={`mx-auto ${state === "paid" ? "text-[color:var(--bl-blue)]" : state === "pending" ? "text-white/60 animate-spin" : "text-[color:var(--bl-gold)]"}`} />
        <div className="bl-eyebrow mt-8 inline-flex items-center gap-2"><Bitcoin size={12} /> {copy.eyebrow}</div>
        <h1 className="bl-heading text-white text-4xl mt-4" data-testid="crypto-return-heading">{copy.title}</h1>
        <p className="text-white/60 mt-4">{copy.body}</p>

        {status && (
          <div className="mt-8 border border-white/10 bg-white/[0.02] p-5 text-left" data-testid="crypto-order-card">
            <div className="bl-eyebrow text-white/40">Order reference</div>
            <div className="font-mono text-white text-xs break-all mt-1">{status.order_id}</div>
            <div className="mt-3 flex items-baseline justify-between">
              <div className="bl-eyebrow text-white/40">Total</div>
              <div className="font-display text-white text-xl">A${(status.total || 0).toFixed(2)}</div>
            </div>
            {state === "pending" && status.checkout_url && (
              <a href={status.checkout_url} data-testid="crypto-open-invoice" className="mt-4 bl-btn gold w-full inline-flex items-center justify-center gap-2 text-xs">
                Open Bitcoin invoice <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
        {error && <div data-testid="crypto-return-error" className="mt-6 border border-red-500/40 bg-red-500/5 text-red-200 text-sm px-4 py-3">{error}</div>}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" className="bl-btn">{state === "expired" ? "Back to Cart" : "Continue Shopping"}</Link>
          {orderId && state === "paid" && <Link to={`/track/${orderId}`} className="bl-btn gold" data-testid="crypto-track-order">Track Order</Link>}
          <Link to="/account" className="bl-btn primary">View Account</Link>
        </div>
      </div>
    </div>
  );
}
